import { useMemo, useState } from 'react';
import type { OverviewCollector, TierBand, TierScope } from '@/api/onderaannemers';
import type { Meerwerk } from '@/api/meerwerken';
import { MEERWERK_TYPE_LABELS } from '@/api/meerwerken';
import { GeenPrijsLabel } from '@/components/ui/GeenPrijsLabel';

// Dutch label for a supplier price tier (ADR-050). 'from200' is the cheaper bulk
// rate at ≥200 m²; 'under200' is the base rate.
function tierLabelNl(tier: TierBand): string {
  return tier === 'from200' ? '200+ tarief' : 'basistarief';
}

// 'Materiaallijst' for picking scope, 'Projecttotaal' for the project fallback.
function scopePrefixNl(scope: TierScope): string {
  return scope === 'materiaallijst' ? 'Materiaallijst' : 'Projecttotaal';
}

function collectorWord(n: number): string {
  return `collector${n !== 1 ? 'en' : ''}`;
}

// Dutch label for a manual-priced system in the rate inputs. 'onbekend' is the sentinel for a
// kring whose system was left blank in Odoo.
function systeemLabelNl(sys: string): string {
  if (sys === 'onbekend') return 'Niet ingevuld';
  return sys.length > 0 ? sys[0].toUpperCase() + sys.slice(1) : sys;
}

export interface TierGroup {
  key: string;
  scope: TierScope;
  label: string;
  basisM2: number;
  tier: TierBand;
  count: number;
  // The collectors in this group — used to flatten a group-level tier override down to
  // the per-collector map the API expects.
  collectorIds: number[];
}

// Group the selected collectors by their (scope, label) basis so the confirm step
// shows one line per materiaallijst/project, mirroring the server tierInfo. basisM2
// is identical across collectors in a group (it is the shared basis), so the first
// wins. Counts the collectors being added in each group.
export function groupSelectedByTier(
  collectors: OverviewCollector[],
  selected: Set<number>,
): TierGroup[] {
  const groups = new Map<string, TierGroup>();
  for (const c of collectors) {
    if (!selected.has(c.id)) continue;
    const key = `${c.tierScope}:${c.tierLabel}`;
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      existing.collectorIds.push(c.id);
    } else {
      groups.set(key, {
        key,
        scope: c.tierScope,
        label: c.tierLabel,
        basisM2: c.basisM2,
        tier: c.tier,
        count: 1,
        collectorIds: [c.id],
      });
    }
  }
  return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label, 'nl'));
}

interface AppendConfirmModalProps {
  installerName: string;
  selectedCollectors: OverviewCollector[];
  // How many of the selected collectors are marked "al geplaatst" (minprijs deduction).
  // The row checkbox is the source of truth; this is display-only here.
  geplaatstCount: number;
  // Vendor charges one flat rate (no base-rate tier) → show "vast tarief", not a band (TD-080).
  vendorSingleRate: boolean;
  appending: boolean;
  // Meerwerken already selected inline in the cockpit body — shown here read-only for
  // confirmation (selection happens in the body now, like collectors).
  selectedMeerwerken?: Meerwerk[];
  // Receives the per-collector tier overrides the user flipped in the popup (empty when none)
  // plus the typed €/m² rate per manual-priced system (empty when there are none).
  onConfirm: (
    tierOverrides: Record<number, TierBand>,
    customSystemRates: Record<string, number>,
  ) => void;
  onCancel: () => void;
}

export function AppendConfirmModal({
  installerName,
  selectedCollectors,
  geplaatstCount,
  vendorSingleRate,
  appending,
  selectedMeerwerken = [],
  onConfirm,
  onCancel,
}: AppendConfirmModalProps) {
  const groups = useMemo(
    () => groupSelectedByTier(selectedCollectors, new Set(selectedCollectors.map((c) => c.id))),
    [selectedCollectors],
  );
  const total = selectedCollectors.length;

  // Collectors with no materiaallijst link (tierScope 'project' ⟺ x_studio_materiaallijst_id
  // is false, routes/onderaannemers.ts). Billed on the project total instead of the
  // materiaallijst basis (ADR-050) → possibly the wrong supplierinfo tier. Warn-only: we
  // surface it loudly at the money moment but never block the confirm (Igor's call).
  const unlinkedCollectors = useMemo(
    () => selectedCollectors.filter((c) => c.tierScope === 'project'),
    [selectedCollectors],
  );

  // Distinct manual-priced systems across the selection (warp, infrezen, blank, …). Each needs
  // a typed €/m² rate before the PO can be made — the server rejects the batch otherwise.
  const unpricedSystems = useMemo(() => {
    const set = new Set<string>();
    for (const c of selectedCollectors) for (const s of c.unpricedSystems ?? []) set.add(s);
    return [...set].sort();
  }, [selectedCollectors]);

  // Ephemeral per-group tier override, keyed by group.key. Unset → use the server-computed
  // tier. The modal unmounts on close, so this resets every time the popup re-opens.
  const [overrides, setOverrides] = useState<Record<string, TierBand>>({});
  const effectiveTier = (g: TierGroup): TierBand => overrides[g.key] ?? g.tier;

  // Typed €/m² rate per manual-priced system (raw input strings, parsed on confirm).
  const [rates, setRates] = useState<Record<string, string>>({});
  const parseRate = (raw: string | undefined): number | null => {
    if (raw === undefined || raw.trim() === '') return null;
    const v = Number(raw.replace(',', '.'));
    if (!Number.isFinite(v)) return null;
    // Reject anything that rounds to €0 (e.g. 0.004) so the gate stays closed instead of
    // sending a 0 the server would reject — the server is still the authoritative guard.
    const rounded = Math.round(v * 100) / 100;
    return rounded > 0 ? rounded : null;
  };
  // A blank/invalid €/m² is allowed: that system books at €0 "prijs te bepalen" and shows red
  // in the Lopende PO's overview for later correction. So rates never gate the confirm — only
  // that something is selected.
  const anyRateBlank = unpricedSystems.some((s) => parseRate(rates[s]) === null);

  // At least one collector or one meerwerk must be selected to confirm.
  const canConfirm = total > 0 || selectedMeerwerken.length > 0;

  // Flatten group overrides → per-collector map, only for groups flipped away from auto.
  const handleConfirm = () => {
    if (!canConfirm) return;
    const map: Record<number, TierBand> = {};
    for (const g of groups) {
      if (vendorSingleRate) continue; // no band choice for flat-rate vendors
      const eff = effectiveTier(g);
      if (eff !== g.tier) {
        for (const id of g.collectorIds) map[id] = eff;
      }
    }
    const customSystemRates: Record<string, number> = {};
    for (const s of unpricedSystems) {
      const v = parseRate(rates[s]);
      if (v !== null) customSystemRates[s] = v;
    }
    onConfirm(map, customSystemRates);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="append-confirm-title"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 id="append-confirm-title" className="text-base font-bold text-td-navy">
            Toevoegen aan PO — {installerName}
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {total > 0 && `${total} ${collectorWord(total)}`}
            {total > 0 && selectedMeerwerken.length > 0 && ' + '}
            {selectedMeerwerken.length > 0 && `${selectedMeerwerken.length} meerwerk${selectedMeerwerken.length !== 1 ? 'en' : ''}`}
            {' '}{total + selectedMeerwerken.length === 1 ? 'wordt' : 'worden'} toegevoegd.{' '}
            {total > 0 && (vendorSingleRate ? 'Controleer het tarief.' : 'Controleer en pas zo nodig het tarief per groep aan.')}
          </p>
          {geplaatstCount > 0 && (
            <p className="text-xs font-medium text-amber-700 mt-1">
              {geplaatstCount} al geplaatst — minprijs wordt verrekend.
            </p>
          )}
        </div>

        <div className="px-5 py-4 space-y-2 max-h-[50vh] overflow-y-auto">
          {groups.map((g) => (
            <div
              key={g.key}
              className="flex items-start justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-td-navy truncate">
                  {scopePrefixNl(g.scope)} {g.label}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {vendorSingleRate
                    ? `${g.count} ${collectorWord(g.count)}`
                    : `${Math.round(g.basisM2)} m² · ${g.count} ${collectorWord(g.count)}`}
                </p>
              </div>
              {vendorSingleRate ? (
                <span className="flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full bg-td-blue/10 text-td-blue">
                  vast tarief
                </span>
              ) : (
                <div className="flex-shrink-0 inline-flex rounded-full bg-gray-100 p-0.5" role="group" aria-label="Tarief kiezen">
                  {(['under200', 'from200'] as const).map((band) => {
                    const active = effectiveTier(g) === band;
                    const activeClasses = band === 'from200'
                      ? 'bg-td-green/15 text-td-green shadow-sm'
                      : 'bg-white text-td-navy shadow-sm';
                    return (
                      <button
                        key={band}
                        type="button"
                        onClick={() => setOverrides((o) => ({ ...o, [g.key]: band }))}
                        aria-pressed={active}
                        className={`text-xs font-semibold px-2.5 py-0.5 rounded-full transition-colors ${
                          active ? activeClasses : 'text-gray-400 hover:text-gray-600'
                        }`}
                      >
                        {tierLabelNl(band)}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          {unpricedSystems.length > 0 && (
            <div className="mt-3 pt-3 border-t border-dashed border-gray-200 space-y-2">
              <p className="text-xs font-semibold text-td-navy">
                Systemen zonder vaste prijs — vul €/m² in
              </p>
              <p className="text-xs text-td-muted">
                Leeg laten mag: die regel komt op <span className="font-semibold text-red-600">€0 – prijs te bepalen</span> en licht rood op bij “Lopende PO's”.
              </p>
              {unpricedSystems.map((s) => (
                <div key={s} className="flex items-center justify-between gap-3 rounded-lg bg-amber-50 px-3 py-2">
                  <span className="text-sm font-semibold text-amber-900 truncate">{systeemLabelNl(s)}</span>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-xs text-gray-500">€</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={rates[s] ?? ''}
                      onChange={(e) => setRates((r) => ({ ...r, [s]: e.target.value }))}
                      placeholder="0,00"
                      aria-label={`Prijs per m² voor ${systeemLabelNl(s)}`}
                      className="w-20 text-sm text-right px-2 py-1 rounded-md border border-amber-300 focus:border-td-blue focus:outline-none"
                    />
                    <span className="text-xs text-gray-500">/m²</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedMeerwerken.length > 0 && (
            <SelectedMeerwerkList meerwerken={selectedMeerwerken} />
          )}

          {unlinkedCollectors.length > 0 && (
            <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
              <div className="rounded-lg bg-amber-50 border border-amber-300 px-3 py-2.5">
                <p className="text-xs font-bold text-amber-900">
                  ⚠ {unlinkedCollectors.length}{' '}
                  {collectorWord(unlinkedCollectors.length)} zonder materiaallijst
                </p>
                <p className="text-xs text-amber-800 mt-0.5">
                  Wordt op projecttotaal gefactureerd (mogelijk verkeerd tarief). Koppel een
                  materiaallijst voor een correct onderaannemerstarief.
                </p>
                <ul className="mt-1.5 space-y-0.5">
                  {unlinkedCollectors.map((c) => (
                    <li key={c.id} className="text-xs text-amber-900 font-medium truncate">
                      • {c.name}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={appending}
            className="px-4 py-2 text-sm font-semibold text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={appending || !canConfirm}
            title={anyRateBlank ? 'Systemen zonder ingevulde prijs komen op €0 – prijs te bepalen' : undefined}
            className="px-4 py-2 text-sm font-bold text-white bg-td-blue rounded-lg hover:bg-td-blue/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {appending ? 'Bezig…' : 'Bevestig toevoegen'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── MeerwerkPicker ─────────────────────────────────────────────────────────── */

/* ── SelectedMeerwerkList — read-only confirmation of the meerwerken ticked in the body ── */

function SelectedMeerwerkList({ meerwerken }: { meerwerken: Meerwerk[] }) {
  return (
    <div className="mt-3 pt-3 border-t border-dashed border-gray-200 space-y-2">
      <p className="text-xs font-semibold text-td-navy">
        Meerwerken ({meerwerken.length})
      </p>
      {meerwerken.map((m) => {
        // null = no price (a km line the server would refuse): no amount, no total.
        const effectivePrice = m.effective_unit_price;
        const isKm = m.type === 'kilometervergoeding' || m.unit === 'km';
        const total = effectivePrice === null ? null : m.quantity * effectivePrice;
        const typeLabel = MEERWERK_TYPE_LABELS[m.type] ?? m.type;
        return (
          <div key={m.id} className="flex items-start gap-3 rounded-lg px-3 py-2 bg-gray-50">
            <div className="flex-1 min-w-0">
              {m.project_name && (
                <p className="text-[11px] font-bold text-td-blue truncate">{m.project_name}</p>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-td-navy truncate">{m.name}</span>
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-td-blue/10 text-td-blue">
                  {typeLabel}
                </span>
                {m.date && <span className="text-[10px] text-gray-400">{m.date}</span>}
              </div>
            </div>
            <div className="text-right text-xs text-td-muted flex-shrink-0">
              {effectivePrice === null ? (
                <>
                  <div>{isKm ? `${m.quantity} km` : `${m.quantity}`}</div>
                  <GeenPrijsLabel reason={m.price_unavailable_reason} className="block max-w-[16rem]" />
                </>
              ) : (
                <div>{isKm ? `${m.quantity} km × €${effectivePrice.toFixed(2)}` : `${m.quantity} × €${effectivePrice.toFixed(2)}`}</div>
              )}
              {total !== null && total > 0 && (
                <div className="font-semibold text-td-navy">€{total.toFixed(2)}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
